import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty, safeAreaBottomIsRequired } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  doAseSetupFlagDelete,
} from 'store/actions';
import { selectRemoteAssessmentFlags } from 'store/remote/selectors';

export const FlagsSetupModal: React.FC<
  ModalScreenProps<ModalScreens.AseSetupFlags>
> = () => {
  const dispatch = useDispatch();
  const { bottom } = useSafeAreaInsets();

  const flags = Object.values(useSelector(selectRemoteAssessmentFlags) || {});

  const handleClose = () => {
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleOpenFlag = (flag: (typeof flags)[number]) => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseSetupFlag,
        params: {
          flag,
        },
      }),
    );
  };

  const handleCreateFlag = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseSetupFlag,
        params: {
          flag: null,
        },
      }),
    );
  };

  const handleDeleteFlag = (flag: (typeof flags)[number]) =>
    dispatch(doAseSetupFlagDelete({ flagId: flag.id, name: flag.name }));

  useEsc(handleClose);

  return (
    <>
      <PageScrollView
        header={
          <NavigationHeader
            heading="Flags"
            onPressSaveButton={handleClose}
            saveButtonText="Done"
          />
        }
        pageStyle={{ flex: 1 }}
      >
        {flags.length ? (
          <Card>
            {flags.map((flag) => {
              const availableIn = [
                flag.setInProgress ? 'In Progress' : null,
                flag.setSubmitted ? 'Submitted' : null,
                flag.setApproved ? 'Approved' : null,
              ].filter(filterArrayEmpty);
              return (
                <CardRow
                  key={flag.id}
                  label={flag.name}
                  label2={flag.description}
                  label2NumberOfLines={2}
                  labelAlignTop
                  value={
                    availableIn.length
                      ? `Available: ${availableIn.join(', ')}`
                      : ''
                  }
                  onPress={() => handleOpenFlag(flag)}
                  showMenuOnContext
                  menuOptions={[
                    {
                      text: 'Open Flag',
                      onPress: () => handleOpenFlag(flag),
                    },
                    '-',
                    {
                      text: 'Delete Flag',
                      onPress: () => handleDeleteFlag(flag),
                      destructive: true,
                      icon: ICONS.TrashCanLight,
                    },
                  ]}
                />
              );
            })}
          </Card>
        ) : (
          <PageEmpty
            icon={ICONS.FlagLight}
            text="No Flags"
            style={{ flex: 1 }}
          />
        )}
      </PageScrollView>
      <View
        style={{
          paddingTop: theme.paddingHorizontal,
          paddingBottom:
            theme.paddingHorizontal +
            (safeAreaBottomIsRequired(true) ? bottom : 0),
          borderTopWidth: 1,
          borderTopColor: '#dddddd',
        }}
      >
        <Card margin={false}>
          <CardRow
            label="Add Flag"
            icon={ICONS.PlusLight}
            labelColor={theme.colors.blue}
            iconColor={theme.colors.blue}
            onPress={handleCreateFlag}
            compact
          />
        </Card>
      </View>
    </>
  );
};
